import axiosInstance from '../axiosConfig';
import {
  getOfflineQueue,
  updateOfflineOperation,
  removeOfflineOperation,
} from './useOfflineQueue';
import { removeOfflineMeasurement } from './useOfflineMeasurements';
import { setOfflineUuidMapping, resolveOfflineUuid } from './useOfflineUuidMap';
import { getOfflineFileBlob, deleteOfflineFileBlob } from './useOfflineFileBlobs';
import { setOfflineMeasurementInfoUuidMapping, resolveOfflineMeasurementInfoUuid } from './useOfflineMeasurementInfoUuidMap';

export interface OfflineQueueSyncResult {
  success: number;
  failed: number;
}

export const syncOfflineQueue = async (): Promise<OfflineQueueSyncResult> => {
  if (!navigator.onLine) {
    return { success: 0, failed: getOfflineQueue().length };
  }

  const operations = getOfflineQueue().filter(
    (op) => op.status === 'pending' || op.status === 'failed'
  );

  if (operations.length === 0) {
    return { success: 0, failed: 0 };
  }

  let success = 0;
  let failed = 0;

  const idsToSkip = new Set<string>();

  const measurementOps = operations.filter(
    (op) => op.entityType === 'measurement'
  );


  for (const postOp of measurementOps) {
    if (postOp.method !== 'POST' || !postOp.tempId) continue;

    const tempId = postOp.tempId;

    const matchingDelete = measurementOps.find((op) => {
      if (op.method !== 'DELETE') return false;
      const url = op.url || '';
      const uuidParam = 'measurementUuid=';
      const idx = url.indexOf(uuidParam);
      if (idx === -1) return false;
      const value = decodeURIComponent(url.slice(idx + uuidParam.length));
      return value === tempId;
    });

    if (matchingDelete) {
      idsToSkip.add(postOp.id);
      idsToSkip.add(matchingDelete.id);
    }
  }

  if (idsToSkip.size > 0 && idsToSkip.size === operations.length) {
    idsToSkip.forEach((id) => {
      removeOfflineOperation(id);
      success += 1;
    });

    return { success, failed };
  }

  if (idsToSkip.size > 0) {
    idsToSkip.forEach((id) => {
      removeOfflineOperation(id);
      success += 1;
    });
  }

  const effectiveOperations = operations.filter(
    (op) => !idsToSkip.has(op.id)
  );

  // Kolejność ma znaczenie:
  // - /files (POST) zależy od istnienia realnego parentUuid (measurementInfo.uuid)
  //   które powstaje po zsynchronizowaniu PUT /measurement/info i zapisaniu mapy temp->real.
  // Dlatego nadajemy priorytety zamiast polegać 100% na createdAt.
  const getPriority = (op: any) => {
    // 0) tworzenie pomiaru
    if (op.entityType === 'measurement' && op.method === 'POST' && op.url === '/measurement') {
      return 0;
    }

    // 1) zapis measurementInfo (generuje mapowanie tempMeasurementInfoUuid -> real)
    if (op.entityType === 'measurement' && op.method === 'PUT' && op.url?.includes('/measurement/info')) {
      return 1;
    }

    // 2) pozostałe operacje pomiaru
    if (op.entityType === 'measurement') {
      return 2;
    }

    // 3) upload plików na końcu
    if (op.entityType === 'file' && op.method === 'POST') {
      return 3;
    }

    // 4) reszta
    return 4;
  };

  const sorted = [...effectiveOperations].sort((a: any, b: any) => {
    const pa = getPriority(a);
    const pb = getPriority(b);
    if (pa !== pb) return pa - pb;
    return a.createdAt - b.createdAt;
  });

  for (const op of sorted) {
    updateOfflineOperation(op.id, {
      status: 'processing',
      lastTriedAt: Date.now(),
      lastError: null,
    });

    try {
      // Specjalny case: upload plików (multipart). W offline zapisujemy blob w IndexedDB pod kluczem op.tempId.
      if (op.entityType === 'file' && op.method === 'POST') {
        if (!op.tempId) {
          throw new Error('Missing tempId for offline file operation');
        }

        const blobRec = await getOfflineFileBlob(op.tempId);
        if (!blobRec) {
          throw new Error('Offline file blob not found for operation');
        }

        // Mapowanie parentUuid (measurementInfo.uuid) z temp -> real
        let resolvedUrl = op.url;
        try {
          const urlObj = new URL(op.url, 'http://dummy');
          const parentUuid = urlObj.searchParams.get('parentUuid');
          const kind = (op.body as any)?.offlineParentKind;

          if (parentUuid && kind === 'measurementInfo') {
            const realParentUuid = resolveOfflineMeasurementInfoUuid(parentUuid);
            urlObj.searchParams.set('parentUuid', realParentUuid);
            resolvedUrl = urlObj.pathname + (urlObj.search ? urlObj.search : '');
          }
        } catch {
          // fallback: zostawiamy jak jest
        }

        // URL już zawiera parentUuid i type.
        const formData = new FormData();
        formData.append('files', blobRec.blob, blobRec.name);

        await axiosInstance.post(resolvedUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        await deleteOfflineFileBlob(op.tempId);

        removeOfflineOperation(op.id);
        success += 1;
        continue;
      }

      const resolvedBody =
        op.body && typeof op.body === 'object'
          ? {
              ...op.body,
              measurementUuid: (op.body as any).measurementUuid
                ? resolveOfflineUuid((op.body as any).measurementUuid)
                : (op.body as any).measurementUuid,
            }
          : op.body;

      const response = await axiosInstance.request({
        method: op.method,
        url: op.url,
        data: resolvedBody,
      });

      // Jeśli to był PUT /measurement/info w offline, to backend zwraca realne uuid measurementInfo.
      // Zapisujemy mapę tempMeasurementInfoUuid -> realMeasurementInfoUuid, żeby upload /files trafił w istniejącego parenta.
      if (
        op.entityType === 'measurement' &&
        op.method === 'PUT' &&
        op.url?.includes('/measurement/info') &&
        op.tempId
      ) {
        const realMeasurementInfoUuid = (response?.data as any)?.measurement?.measurementInfo?.uuid;
        if (realMeasurementInfoUuid) {
          // op.tempId to temp uuid measurementInfo, które wygenerowaliśmy w simulateResponse
          setOfflineMeasurementInfoUuidMapping(op.tempId, realMeasurementInfoUuid);
        }
      }

      // Jeśli to był POST /measurement w offline, to backend zwróci realne uuid.
      // Zapisujemy mapowanie tempUuid -> realUuid, żeby kolejne operacje (client-info itd.)
      // mogły wysłać właściwe measurementUuid.
      if (op.entityType === 'measurement' && op.method === 'POST' && op.tempId) {
        const realUuid = (response?.data as any)?.measurement?.uuid;
        if (realUuid) {
          setOfflineUuidMapping(op.tempId, realUuid);
        }
      }

      if (op.entityType === 'measurement' && op.method === 'POST') {
        const orderUuid = (op.body as any)?.orderUuid;
        const tempUuid = op.tempId;
        if (orderUuid && tempUuid) {
          removeOfflineMeasurement(orderUuid, tempUuid);
        }
      }

      removeOfflineOperation(op.id);
      success += 1;
    } catch (error: any) {
      console.error('❌ Error syncing offline operation', op, error);
      const message = error?.message || 'Unknown error';
      updateOfflineOperation(op.id, {
        status: 'failed',
        lastError: message,
      });
      failed += 1;
    }
  }

  return { success, failed };
};
